import { ACTION_TYPES } from '@/util/access_visibility'

export interface ActionPermissions {
  actionType: string
  contentType?: string
  contentTitle?: string
  contentUuid?: string
  blockUnauthenticated?: boolean
  blockTrial?: boolean
  blockExpired?: boolean
  blockQuota?: boolean
  block?: boolean
}

export type LimitActionButtons = 'auth' | 'plans' | 'ok' | 'none'

const SIGN_UP_FOR_TRIAL = 'Sign up for a Goalbook Toolkit trial account'
const MUST_HAVE_ACTIVE_MEMBERSHIP = 'you must have an active membership through your school or district'
const CONTACT_ADMIN_TO_RENEW = 'Contact your administrator to purchase or renew your premium membership.'
export const UNAUTHORIZED_MESSAGE = 'You are not authorized to perform this action.'

const QUOTA_TITLES: Record<string, string> = {
  [ACTION_TYPES.DOWNLOAD]: 'Monthly download limit reached',
  [ACTION_TYPES.COPY]: 'Monthly copy limit reached',
  [ACTION_TYPES.PRINT]: 'Monthly print limit reached'
}

type Candidate = [flag: boolean | undefined, message: string]

const firstApplicable = (candidates: Candidate[]): string =>
  candidates.find(([flag]) => flag)?.[1] ?? UNAUTHORIZED_MESSAGE

const membershipRequired = (toDoThis: string) => `${toDoThis}, ${MUST_HAVE_ACTIVE_MEMBERSHIP}.`
const membershipRenewal = (toDoThis: string) =>
  `${toDoThis}, ${MUST_HAVE_ACTIVE_MEMBERSHIP}. ${CONTACT_ADMIN_TO_RENEW}`

const MESSAGES: Record<string, (p: ActionPermissions) => string> = {
  [ACTION_TYPES.SAVE]: (p) =>
    firstApplicable([
      [p.blockUnauthenticated, `${SIGN_UP_FOR_TRIAL} to save this page.`],
      [p.blockExpired, 'To save this page, you must have an active membership.']
    ]),
  [ACTION_TYPES.TRANSLATE]: (p) =>
    firstApplicable([
      [p.blockUnauthenticated, `${SIGN_UP_FOR_TRIAL} to translate this content.`],
      [p.blockExpired, 'To translate this content, you must have an active membership.']
    ]),
  [ACTION_TYPES.COPY_STUDENT]: () => membershipRenewal('To copy a folder'),
  [ACTION_TYPES.DOWNLOAD]: (p) =>
    firstApplicable([
      [p.blockUnauthenticated, `${SIGN_UP_FOR_TRIAL} to download this resource.`],
      [p.blockTrial, membershipRequired('To download this resource')],
      [
        p.blockQuota,
        'You have already used all of your available downloads for this month. If you believe this is an error, contact us at support@goalbook.com.'
      ],
      [p.blockExpired, membershipRenewal('To download this resource')]
    ]),
  [ACTION_TYPES.COPY]: (p) =>
    firstApplicable([
      [
        p.blockQuota,
        'You have already used all of your available copies for this month. We have a quota in place to make sure our content is not being abused. If you believe this to be an error, contact us at support@goalbook.com.'
      ],
      [p.blockExpired, `To copy this text ${MUST_HAVE_ACTIVE_MEMBERSHIP}. ${CONTACT_ADMIN_TO_RENEW}`]
    ]),
  [ACTION_TYPES.PRINT]: (p) =>
    firstApplicable([
      [p.blockUnauthenticated, `${SIGN_UP_FOR_TRIAL} to print this content.`],
      [p.blockTrial, membershipRequired('To print this content')],
      [
        p.blockQuota,
        'You have already used all of your available prints for this month. If you believe this is an error, contact us at support@goalbook.com.'
      ],
      [p.blockExpired, membershipRenewal('To print this content')]
    ]),
  [ACTION_TYPES.SHARE]: (p) =>
    firstApplicable([
      [p.blockTrial, membershipRequired('To share this content')],
      [p.blockExpired, membershipRenewal('To share this content')]
    ]),
  // Expiry is tested before trial here, unlike print and download: an expired trial licence
  // raises both flags for generation, and the title already resolves that pair as expiry.
  [ACTION_TYPES.GENERATE_ASSESSMENT]: (p) =>
    firstApplicable([
      [p.blockUnauthenticated, `${SIGN_UP_FOR_TRIAL} to generate an assessment.`],
      [p.blockExpired, membershipRenewal('To generate an assessment')],
      [p.blockTrial, membershipRequired('To generate an assessment')]
    ]),
  [ACTION_TYPES.DEVELOP_PLW]: (p) =>
    firstApplicable([
      [p.blockUnauthenticated, `${SIGN_UP_FOR_TRIAL} to use the Present Levels Wizard.`],
      [p.blockExpired, membershipRenewal('To use the Present Levels Wizard')]
    ])
}

export function limitActionTitle(p: ActionPermissions): string {
  if (p.blockUnauthenticated) return 'You must be signed in to use this feature'
  if (p.blockExpired) return 'Your membership has expired'
  if (p.blockTrial) return 'Feature available with premium membership'
  if (p.blockQuota && QUOTA_TITLES[p.actionType]) return QUOTA_TITLES[p.actionType]
  return 'Unauthorized'
}

export function limitActionMessage(p: ActionPermissions): string {
  return MESSAGES[p.actionType]?.(p) ?? UNAUTHORIZED_MESSAGE
}

export function limitActionButtons(p: ActionPermissions): LimitActionButtons {
  if (p.blockUnauthenticated) return 'auth'
  if (p.blockTrial || p.blockExpired) return 'plans'
  if (p.blockQuota) return 'ok'
  return 'none'
}
